// src/features/task/components/TaskListToolbar.tsx
// Filter + Sort 合體工具列，緊貼 task list 上方
//
// [All] [Open] [Closed]  ────────  [Sort ↓]（dropdown，桌面行動一致）

import React, { useState, useRef } from 'react';
import { ArrowDown, ArrowUp, SlidersHorizontal } from 'lucide-react';
import useClickOutside from '../../../hooks/useClickOutside';

export type TaskSortKey = 'createdAt' | 'priority' | 'goal' | 'smart';
export type SortDirection = 'asc' | 'desc';
export type StatusFilter = 'OPEN' | 'CLOSED';

interface TaskListToolbarProps {
  statusFilter: StatusFilter;
  onStatusChange: (status: StatusFilter) => void;
  openCount: number;
  closedCount: number;
  sortKey: TaskSortKey;
  sortDir: SortDirection;
  onSortChange: (key: TaskSortKey, dir: SortDirection) => void;
}

const SORT_OPTIONS: { key: TaskSortKey; label: string; supportsDirection: boolean }[] = [
  { key: 'smart', label: 'Smart', supportsDirection: false },
  { key: 'createdAt', label: 'Date', supportsDirection: true },
  { key: 'priority', label: 'Priority', supportsDirection: false },
  { key: 'goal', label: 'Goal', supportsDirection: false },
];

type FilterTab = { key: StatusFilter; label: string; countKey: keyof Pick<TaskListToolbarProps, 'openCount' | 'closedCount'> };

const FILTER_TABS: FilterTab[] = [
  { key: 'OPEN', label: 'Open', countKey: 'openCount' },
  { key: 'CLOSED', label: 'Closed', countKey: 'closedCount' },
];

const TaskListToolbar: React.FC<TaskListToolbarProps> = ({
  statusFilter,
  onStatusChange,
  openCount,
  closedCount,
  sortKey,
  sortDir,
  onSortChange,
}) => {
  const counts = { openCount, closedCount };
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useClickOutside(dropdownRef, () => setDropdownOpen(false), dropdownOpen);

  function handleSortClick(key: TaskSortKey, supportsDirection: boolean) {
    if (key === sortKey && supportsDirection) {
      onSortChange(key, sortDir === 'desc' ? 'asc' : 'desc');
    } else {
      onSortChange(key, 'desc');
    }
    setDropdownOpen(false);
  }

  const activeSortLabel = SORT_OPTIONS.find(o => o.key === sortKey)?.label ?? 'Sort';
  const activeSupportsDir = SORT_OPTIONS.find(o => o.key === sortKey)?.supportsDirection ?? false;

  return (
    <div className="flex items-center justify-between px-1 py-2">
      {/* Filter pills */}
      <div className="flex gap-1">
        {FILTER_TABS.map(({ key, label, countKey }) => {
          const count = counts[countKey];
          const isActive = statusFilter === key;
          return (
            <button
              key={key}
              onClick={() => onStatusChange(key)}
              className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
                isActive
                  ? 'bg-sky-500 text-white'
                  : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
              }`}
              aria-pressed={isActive}
              aria-label={`Show ${label.toLowerCase()} tasks`}
            >
              {label}
              {count > 0 && <span className="ml-1 opacity-70">({count})</span>}
            </button>
          );
        })}
      </div>

      {/* Sort dropdown（桌面行動一致） */}
      <div className="relative" ref={dropdownRef}>
        <button
          onClick={() => setDropdownOpen(v => !v)}
          className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium transition-colors ${
            dropdownOpen
              ? 'bg-sky-500 text-white'
              : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
          }`}
          aria-label="Sort options"
          aria-expanded={dropdownOpen}
        >
          <SlidersHorizontal size={14} />
          <span>{activeSortLabel}</span>
          {activeSupportsDir && (
            sortDir === 'desc' ? <ArrowDown size={11} /> : <ArrowUp size={11} />
          )}
        </button>

        {dropdownOpen && (
          <div className="absolute right-0 top-full mt-1 bg-white border border-gray-200 rounded-xl shadow-lg z-dropdown min-w-[120px] py-1 overflow-hidden">
            {SORT_OPTIONS.map(({ key, label, supportsDirection }) => {
              const isActive = sortKey === key;
              return (
                <button
                  key={key}
                  onClick={() => handleSortClick(key, supportsDirection)}
                  className={`w-full flex items-center justify-between px-3 py-2 text-sm transition-colors ${
                    isActive
                      ? 'text-gray-900 font-medium bg-gray-50'
                      : 'text-gray-600 hover:bg-gray-50'
                  }`}
                  aria-label={`Sort by ${label}`}
                >
                  <span>{label}</span>
                  {isActive && supportsDirection && (
                    sortDir === 'desc' ? <ArrowDown size={13} /> : <ArrowUp size={13} />
                  )}
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default TaskListToolbar;
